import { ImportDuplicateStrategy, ImportEntity, ImportTagStrategy, type ImportPreview } from '@paintscout/api'
import { Alert, Button, makeStyles, Radio, Spinner, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined'
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined'
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined'
import classnames from 'classnames'
import FileSaver from 'file-saver'
import React from 'react'
import { countNoun } from '../countNoun'
import RowErrorsPanel from '../RowErrorsPanel'
import {
  getMatchedBy,
  getNoDuplicatesSubtitle,
  getNothingToImportReason,
  getReviewHeadline,
  getReviewState,
  getUpdateSkipNote
} from '../reviewState'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3)
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  sectionHelp: {
    color: theme.palette.text.secondary
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr'
    }
  },
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    borderRadius: theme.borderRadius.md,
    cursor: 'pointer',
    textAlign: 'left'
  },
  cardSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column'
  },
  cardDescription: {
    color: theme.palette.text.secondary
  },
  summary: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  summaryHelp: {
    color: theme.palette.text.secondary
  },
  // First load: the whole body waits, so no section shows and then vanishes (#24977).
  bodyLoading: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 240
  },
  // No duplicates (#25040): centred in the step.
  noDuplicates: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(4, 0)
  },
  noDuplicatesIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 56,
    height: 56,
    marginBottom: theme.spacing(1),
    borderRadius: '50%',
    color: theme.palette.primary.main,
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96
  },
  tiles: {
    display: 'grid',
    gridAutoColumns: 'minmax(0, 180px)',
    gridAutoFlow: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2)
  },
  tile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    borderRadius: theme.borderRadius.lg
  },
  // The sections below the centred block keep their full width.
  fullWidth: {
    alignSelf: 'stretch',
    textAlign: 'left'
  },
  // Match the space above the tiles (column gap + the tiles' marginTop).
  autoLinkSpacing: {
    marginTop: theme.spacing(2)
  },
  // Nothing to import (#25038): a divider, then a card with the reason and the next step.
  nothingDivider: {
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  nothingCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    borderRadius: theme.borderRadius.lg,
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap'
    }
  },
  nothingIcon: {
    color: theme.paintscout.palette.semantic.Warning.S70
  },
  nothingBody: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  // Same plain secondary button as ImportStep's "Import another file": the shared `outlined`
  // variant is a dashed, primary-blue placeholder.
  secondaryAction: {
    '&&': {
      flexShrink: 0,
      color: theme.paintscout.palette.brand.Neutral.P35,
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: theme.paintscout.palette.brand.Neutral.P85
    },
    '&&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70,
      backgroundColor: theme.paintscout.palette.brand.Neutral.P98
    }
  }
}))

/** Shown first to last in the prototype's order. */
export const DUPLICATE_OPTIONS: { value: ImportDuplicateStrategy; label: string; description: string }[] = [
  {
    value: ImportDuplicateStrategy.FillEmpty,
    label: 'Fill empty fields only',
    description: "Merge in new data, don't overwrite existing values."
  },
  {
    value: ImportDuplicateStrategy.Skip,
    label: 'Skip duplicates',
    description: 'Keep the existing record, ignore the imported row.'
  },
  {
    value: ImportDuplicateStrategy.Overwrite,
    label: 'Overwrite existing',
    description: 'Replace existing fields with imported values.'
  },
  {
    value: ImportDuplicateStrategy.ImportAsNew,
    label: 'Import as new',
    description: 'Create a separate record even if it matches.'
  }
]

export const TAG_OPTIONS: { value: ImportTagStrategy; label: string; description: string }[] = [
  {
    value: ImportTagStrategy.Add,
    label: 'Add to existing tags',
    description: 'Keep the tags that are there and add the imported ones.'
  },
  {
    value: ImportTagStrategy.Replace,
    label: 'Replace existing tags',
    description: 'Use the imported tags instead of the ones there now.'
  },
  {
    value: ImportTagStrategy.Ignore,
    label: 'Ignore imported tags',
    description: 'Leave existing tags exactly as they are.'
  }
]

export interface ReviewStepProps {
  entity: ImportEntity
  entityNoun: string
  entityNounPlural: string
  preview?: ImportPreview
  /** The preview from before a duplicate-strategy change; its layout is kept while the new one loads. */
  previousPreview?: ImportPreview
  loading: boolean
  errorMessage?: string
  duplicateStrategy: ImportDuplicateStrategy
  tagStrategy: ImportTagStrategy
  tagsMapped: boolean
  companyMapped: boolean
  onChangeDuplicateStrategy: (strategy: ImportDuplicateStrategy) => void
  onChangeTagStrategy: (strategy: ImportTagStrategy) => void
  /** Nothing to import: back to Upload with a fresh wizard. */
  onUploadAnother: () => void
  /**
   * The Owner picker, for support and superadmin only (`ImportOwnerSelect`). Shown last, just above
   * the Import button, on every screen that has one.
   */
  ownerSelect?: React.ReactNode
}

interface RadioCardProps {
  selected: boolean
  label: string
  description: string
  onSelect: () => void
}

function RadioCard({ selected, label, description, onSelect }: RadioCardProps) {
  const classes = useStyles()

  return (
    <label className={classnames(classes.card, { [classes.cardSelected]: selected })}>
      <Radio checked={selected} onChange={onSelect} inputProps={{ 'aria-label': label }} />
      <span className={classes.cardBody}>
        <Typography variant="body1">{label}</Typography>
        <Typography variant="body2" className={classes.cardDescription}>
          {description}
        </Typography>
      </span>
    </label>
  )
}

/**
 * Step 3: choose how duplicates and tags are handled, then confirm the counts.
 *
 * The duplicate strategy changes the plan, so the parent re-runs `importPreview` when it changes.
 * The tag strategy does not affect the counts, so it needs no round trip.
 *
 * Which screen shows is `getReviewState`'s job (`reviewState.ts`): loading, no duplicates (#25040),
 * nothing to import (#25038), or the strategy cards with the counts.
 *
 * A section is hidden only when its choice cannot change what the import does. The strategy value
 * is still sent; the server ignores it in these cases:
 * - Duplicates, when the preview has `duplicateCount === 0` (#24977): nothing to collapse and
 *   nothing that matches an existing record. `duplicateCount` is the same under every strategy
 *   (#24961), so this cannot change when the strategy changes.
 * - Tags, unless the duplicate strategy is Fill empty or Overwrite (#24976), and with no
 *   duplicates: the tag strategy is only read when a row updates an existing record (`merge.ts`'s
 *   `mergeForUpdate`). Import as new creates every row and Skip never updates a match
 *   (`dedup.ts`'s `classifyUnit`). Repeats inside the file union their tags, or keep the first
 *   row's under Skip, without the tag strategy (`collapseUnits`).
 */
export default function ReviewStep({
  entity,
  entityNoun,
  entityNounPlural,
  preview,
  previousPreview,
  loading,
  errorMessage,
  duplicateStrategy,
  tagStrategy,
  tagsMapped,
  companyMapped,
  onChangeDuplicateStrategy,
  onChangeTagStrategy,
  onUploadAnother,
  ownerSelect
}: ReviewStepProps) {
  const classes = useStyles()
  const isContact = entity === ImportEntity.Contact
  const nouns = { noun: entityNoun, nounPlural: entityNounPlural }
  const { view, layoutPreview, reloading } = getReviewState({
    preview,
    previousPreview,
    loading,
    hasError: !!errorMessage
  })

  if (view === 'error') {
    return <Alert severity="error">{errorMessage}</Alert>
  }

  if (view === 'loading' || !layoutPreview) {
    return (
      <div className={classes.bodyLoading} data-testid="review-loading">
        <Spinner />
      </div>
    )
  }

  // Counts come only from a loaded preview; while reloading, the previous one only sets the layout.
  const loaded = reloading ? undefined : layoutPreview
  const errors = loaded?.errors ?? []
  const companiesToCreate = loaded?.companiesToCreate ?? 0
  const companiesToLink = loaded?.companiesToLink ?? 0
  const showAutoLink = isContact && companyMapped && (companiesToCreate > 0 || companiesToLink > 0)
  const hasDuplicates = layoutPreview.duplicateCount > 0
  const tagStrategyApplies =
    duplicateStrategy === ImportDuplicateStrategy.FillEmpty || duplicateStrategy === ImportDuplicateStrategy.Overwrite
  const showTags = tagsMapped && hasDuplicates && tagStrategyApplies
  // Both describe rows in the file and are the same under every strategy (#24961).
  const duplicatesExisting = layoutPreview.duplicatesExisting
  const duplicatesInFile = layoutPreview.duplicatesInFile

  function handleDownloadErrors() {
    if (!loaded?.errorRowsCsv) {
      return
    }
    FileSaver.saveAs(
      new Blob([loaded.errorRowsCsv], { type: 'text/csv;charset=utf-8' }),
      `${entity.toLowerCase()}-import-errors.csv`
    )
  }

  const autoLink = showAutoLink && (
    <Alert severity="info">
      Contacts will be linked to the companies in your file:{' '}
      {countNoun(companiesToCreate, 'new company', 'new companies')} will be created, {companiesToLink}{' '}
      {companiesToLink === 1 ? 'matches' : 'match'} existing.
    </Alert>
  )

  const errorsPanel = errors.length > 0 && (
    <RowErrorsPanel
      title={`${countNoun(errors.length, 'row', 'rows')} will be skipped`}
      errors={errors}
      onDownload={loaded?.errorRowsCsv ? handleDownloadErrors : undefined}
      data-testid="review-errors"
    />
  )

  if (view === 'noDuplicates' && loaded) {
    const tagCount = loaded.tagCount ?? 0
    const Icon = isContact ? PersonAddOutlinedIcon : BusinessOutlinedIcon

    return (
      <div className={classes.noDuplicates} data-testid="review-no-duplicates">
        <div className={classes.noDuplicatesIcon}>
          <Icon />
        </div>
        <Typography variant="h3">No duplicates found</Typography>
        <Typography variant="body1" className={classes.sectionHelp}>
          {getNoDuplicatesSubtitle(loaded, entity, nouns)}
        </Typography>
        <div className={classes.tiles}>
          <div className={classes.tile} data-testid="review-tile-new">
            <Typography variant="h2">{loaded.willCreate}</Typography>
            <Typography variant="body1" className={classes.sectionHelp}>
              New {loaded.willCreate === 1 ? entityNoun : entityNounPlural}
            </Typography>
          </div>
          {tagCount > 0 && (
            <div className={classes.tile} data-testid="review-tile-tags">
              <Typography variant="h2">{tagCount}</Typography>
              <Typography variant="body1" className={classes.sectionHelp}>
                {tagCount === 1 ? 'Tag' : 'Tags'}
              </Typography>
            </div>
          )}
        </div>
        {autoLink && <div className={classnames(classes.fullWidth, classes.autoLinkSpacing)}>{autoLink}</div>}
        {errorsPanel && <div className={classes.fullWidth}>{errorsPanel}</div>}
        {ownerSelect && <div className={classnames(classes.fullWidth, classes.autoLinkSpacing)}>{ownerSelect}</div>}
      </div>
    )
  }

  const updateSkipNote = loaded && getUpdateSkipNote(loaded, duplicateStrategy, nouns)

  return (
    <div className={classes.root}>
      {hasDuplicates && (
        <div className={classes.section}>
          <Typography variant="h4">Duplicate {entityNounPlural}</Typography>
          <Typography variant="body2" className={classes.sectionHelp}>
            When a {entityNoun} already exists in PaintScout, matched by {getMatchedBy(entity)}.
          </Typography>
          <div className={classes.cards} data-testid="duplicate-options">
            {DUPLICATE_OPTIONS.map((option) => (
              <RadioCard
                key={option.value}
                selected={duplicateStrategy === option.value}
                label={option.label}
                description={option.description}
                onSelect={() => onChangeDuplicateStrategy(option.value)}
              />
            ))}
          </div>
        </div>
      )}

      {showTags && (
        <div className={classes.section}>
          <Typography variant="h4">Tags</Typography>
          <Typography variant="body2" className={classes.sectionHelp}>
            Tags on {entityNounPlural} that already exist.
          </Typography>
          <div className={classes.cards} data-testid="tag-options">
            {TAG_OPTIONS.map((option) => (
              <RadioCard
                key={option.value}
                selected={tagStrategy === option.value}
                label={option.label}
                description={option.description}
                onSelect={() => onChangeTagStrategy(option.value)}
              />
            ))}
          </div>
        </div>
      )}

      {autoLink}

      {errorsPanel}

      {view === 'nothingToImport' && loaded ? (
        <>
          {hasDuplicates && <div className={classes.nothingDivider} />}
          <div className={classes.nothingCard} data-testid="review-nothing-to-import">
            <ReportProblemOutlinedIcon className={classes.nothingIcon} />
            <div className={classes.nothingBody}>
              <Typography variant="h4">Nothing new to import</Typography>
              <Typography variant="body2" className={classes.sectionHelp}>
                {getNothingToImportReason(loaded, nouns)}
              </Typography>
            </div>
            <Button variant="outlined" className={classes.secondaryAction} onClick={onUploadAnother}>
              Upload another file
            </Button>
          </div>
        </>
      ) : (
        <div className={classes.summary} data-testid="review-summary">
          {!loaded ? (
            <Spinner />
          ) : (
            <>
              <Typography variant="body1">
                <strong>{getReviewHeadline(loaded, duplicateStrategy, nouns)}</strong>
              </Typography>
              {updateSkipNote && (
                <Typography variant="body2" className={classes.summaryHelp} data-testid="review-summary-skip-note">
                  {updateSkipNote}
                </Typography>
              )}
              <Typography variant="body2" className={classes.summaryHelp} data-testid="review-summary-breakdown">
                {duplicatesExisting} {duplicatesExisting === 1 ? 'matches' : 'match'} existing{' '}
                {duplicatesExisting === 1 ? entityNoun : entityNounPlural} in PaintScout, {duplicatesInFile}{' '}
                {duplicatesInFile === 1 ? 'repeats' : 'repeat'} within the file.
              </Typography>
            </>
          )}
        </div>
      )}

      {view !== 'nothingToImport' && ownerSelect}
    </div>
  )
}
